<template>
    <div class="page-container">
      <div class="settings-form">
        <h2 class="page-title">AI 接口配置</h2>
  
        <el-form :model="aiForm" label-width="140px" class="compact-form">
          <!-- API Base URL -->
          <el-form-item label="API地址：" class="form-item-row">
            <el-input v-model="aiForm.openai_url" placeholder="如：https://api.openai.com" size="large" clearable class="oss-input">
              <template #prefix><el-icon><location /></el-icon></template>
            </el-input>
          </el-form-item>
  
          <!-- API Key -->
          <el-form-item label="API Key：" class="form-item-row">
            <el-input v-model="aiForm.openai_key" placeholder="请输入密钥（sk-...）" size="large" show-password clearable class="oss-input">
              <template #prefix><el-icon><key /></el-icon></template>
            </el-input>
            <div class="oss-tip">
              <el-icon class="tip-icon"><info-filled /></el-icon>
              <span>建议使用服务商的专用密钥；密钥仅用于当前商户。</span>
            </div>
          </el-form-item>
  
          <!-- 默认模型 -->
          <el-form-item label="默认模型：" class="form-item-row">
            <el-input v-model="aiForm.openai_model" placeholder="如：gpt-4o / qwen2.5-72b-instruct 等" size="large" clearable class="oss-input">
              <template #prefix><el-icon><box /></el-icon></template>
            </el-input>
          </el-form-item>
  
          <!-- 测试连接 -->
          <el-form-item label="连接测试：" class="form-item-row">
            <el-button type="info" size="large" class="test-btn" @click="testConnection" :loading="testing">
              <el-icon class="btn-icon"><connection /></el-icon> 测试AI接口
            </el-button>
            <div class="test-result" v-if="testResult !== null">
              <el-tag :type="testResult ? 'success' : 'danger'" size="large">
                {{ testResult ? '连接成功' : '连接失败' }}
              </el-tag>
              <span class="result-message" v-if="testMessage">{{ testMessage }}</span>
            </div>
          </el-form-item>
  
          <!-- 操作按钮 -->
          <el-form-item class="form-actions">
            <el-button type="primary" size="large" class="submit-btn" @click="submitAIConfig" :loading="loading">
              <el-icon class="btn-icon"><check /></el-icon> 保存配置
            </el-button>
            <el-button type="info" size="large" class="reset-btn" @click="resetAIConfig">
              <el-icon class="btn-icon"><refresh /></el-icon> 重置表单
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios'
  import config from '@/config.js'
  import Cookies from 'js-cookie'
  import { Key, Location, Box, Connection, Check, Refresh, InfoFilled } from '@element-plus/icons-vue'
  
  export default {
    name: 'SystemAIConfig',
    components: { Key, Location, Box, Connection, Check, Refresh, InfoFilled },
    data() {
      return {
        loading: false,
        testing: false,
        testResult: null,
        testMessage: '',
        aiForm: {
          openai_url: '',
          openai_key: '',
          openai_model: ''
        }
      }
    },
    computed: {
      userInfo() {
        return JSON.parse(Cookies.get('user_info') || '{}')
      }
    },
    mounted() {
      this.fetchAIConfig()
    },
    methods: {
      async fetchAIConfig() {
        try {
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/LaAISettingsController.php`, {
            action: 'get_ai',
            store_id: this.userInfo.id
          })
          if (data.success && data.data) {
            this.aiForm = {
              openai_url: data.data.openai_url || '',
              openai_key: data.data.openai_key || '',   // 如果你想后端仅回显“已设置”，这里按需处理
              openai_model: data.data.openai_model || ''
            }
          } else {
            this.$message.warning(data.error || '未获取到AI配置')
          }
        } catch (e) {
          this.$message.error('获取AI配置失败')
          console.error('get_ai error:', e)
        }
      },
  
      async testConnection() {
        if (!this.aiForm.openai_url || !this.aiForm.openai_key) {
          this.$message.warning('请至少填写API地址和API Key')
          return
        }
        this.testing = true
        this.testResult = null
        this.testMessage = ''
        try {
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/LaAISettingsController.php`, {
            action: 'test_ai',
            store_id: this.userInfo.id,
            openai_url: this.aiForm.openai_url,
            openai_key: this.aiForm.openai_key,
            openai_model: this.aiForm.openai_model
          })
          this.testResult = !!data.success
          this.testMessage = data.message || (data.success ? '测试通过' : '测试失败')
          this.$message[data.success ? 'success' : 'error'](this.testMessage)
        } catch (e) {
          this.testResult = false
          this.testMessage = e?.response?.data?.message || e.message || '请求失败'
          this.$message.error('测试失败：' + this.testMessage)
        } finally {
          this.testing = false
        }
      },
  
      async submitAIConfig() {
        if (!this.aiForm.openai_url || !this.aiForm.openai_key) {
          this.$message.warning('API地址与Key为必填')
          return
        }
        this.loading = true
        try {
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/LaAISettingsController.php`, {
            action: 'update_ai',
            store_id: this.userInfo.id,
            ...this.aiForm
          })
          if (data.success) {
            this.$message.success('AI配置保存成功')
            this.fetchAIConfig()
          } else {
            this.$message.error(data.error || '保存失败')
          }
        } catch (e) {
          this.$message.error(e?.response?.data?.error || e.message || '服务器错误')
        } finally {
          this.loading = false
        }
      },
  
      resetAIConfig() {
        this.$confirm('请选择重置方式：', '重置配置', {
          confirmButtonText: '清空表单',
          cancelButtonText: '恢复默认',
          distinguishCancelAndClose: true,
          type: 'warning'
        })
          .then(() => {
            // 清空表单（前端）
            this.clearForm()
          })
          .catch(async (action) => {
            // “恢复默认” => 后端清空并读取
            if (action === 'cancel') {
              await this.clearOnServer()
            }
          })
      },
  
      clearForm() {
        this.aiForm = { openai_url: '', openai_key: '', openai_model: '' }
        this.testResult = null
        this.testMessage = ''
        this.$message.success('表单已清空')
      },
  
      async clearOnServer() {
        try {
          const { data } = await axios.post(`${config.BASE_URL}/shop/api/LaAISettingsController.php`, {
            action: 'clear_ai',
            store_id: this.userInfo.id
          })
          if (data.success) {
            this.$message.success('已恢复默认（清空后端配置）')
            this.fetchAIConfig()
          } else {
            this.$message.error(data.error || '恢复失败')
          }
        } catch (e) {
          this.$message.error('恢复失败：' + (e?.response?.data?.error || e.message))
        }
      }
    }
  }
  </script>
  
  <style scoped lang="scss">
  .page-container { padding: 0px; }
  .settings-form {
    max-width: 95%; margin: 0 auto; padding: 32px 40px; background:#fff; border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  }
  .page-title { font-size: 24px; color:#1f2937; margin-bottom: 32px; font-weight: 600; text-align:center; }
  .compact-form {
    :deep(.el-form-item__label){ font-size:16px; color:#374151; line-height:40px; padding-right:24px!important; }
    .form-item-row{ margin-bottom: 28px; :deep(.el-form-item__content){ flex:1; min-width:0; } }
  }
  .oss-input{ width:100%; max-width:500px; }
  .oss-tip{ margin-top:8px; margin-left:10px; color:#64748b; font-size:13px; display:flex; align-items:center;
    .tip-icon{ color:#3b82f6; margin-right:8px; font-size:16px; }
  }
  .test-btn{ width:200px; padding:14px 24px; .btn-icon{ margin-right:8px; font-size:16px; } }
  .test-result{ margin-top:12px; display:flex; align-items:center; gap:12px; .result-message{ color:#64748b; font-size:13px; } }
  .form-actions{ margin-top:40px; text-align:center;
    .submit-btn,.reset-btn{ width:160px; padding:14px 24px; font-size:15px; letter-spacing:.5px;
      .btn-icon{ margin-right:8px; font-size:16px; }
    }
  }
  </style>
  